import { useQuery } from "@tanstack/react-query";
import { CalendarClock, GitBranch, Rocket, Siren } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import type { ReleaseView } from "../../types";
import { TimeAgo } from "../time-ago";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { formatSchedule } from "./release-meta";

/** Default ship time for a scheduled release: 16:00 Sydney, tomorrow. */
export function defaultScheduleLocal(): string {
  const at = new Date();
  at.setDate(at.getDate() + 1);
  at.setHours(16, 0, 0, 0);
  return toLocalInput(at);
}

function toLocalInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The live latest published release, for context in the dialogs. */
export function LatestReleaseLine() {
  const latest = useQuery({ queryKey: ["latest-release"], queryFn: api.latestRelease, staleTime: 30_000 });
  if (latest.isLoading)
    return <p className="text-xs text-muted-foreground">Looking up the latest release in GitHub…</p>;
  const value = latest.data?.latest;
  if (!value) return <p className="text-xs text-muted-foreground">No published release in GitHub yet.</p>;
  return (
    <p className="text-xs text-muted-foreground">
      Latest in GitHub:{" "}
      <a
        href={value.url}
        target="_blank"
        rel="noreferrer"
        className="font-mono text-foreground hover:underline"
      >
        {value.tag}
      </a>
      {value.publishedAt ? (
        <>
          {" "}
          · published <TimeAgo at={value.publishedAt} />
        </>
      ) : null}
    </p>
  );
}

export function UrgentReleaseDialog({
  open,
  onOpenChange,
  issueIdentifier,
  hasPullRequest,
  defaultFromLastRelease,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Named when the dialog is opened from one issue. */
  issueIdentifier?: string;
  /** A pull request already targets main; the hotfix branch option is then unavailable. */
  hasPullRequest?: boolean;
  defaultFromLastRelease: boolean;
  pending?: boolean;
  onConfirm: (options: { fromLastRelease: boolean }) => void;
}) {
  const [fromLastRelease, setFromLastRelease] = useState(defaultFromLastRelease);
  useEffect(() => {
    if (open) setFromLastRelease(defaultFromLastRelease && !hasPullRequest);
  }, [open, defaultFromLastRelease, hasPullRequest]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Siren className="size-4" /> Urgent release{issueIdentifier ? ` for ${issueIdentifier}` : ""}
          </DialogTitle>
          <DialogDescription>
            Opens its own draft release in GitHub and publishes it the moment the issue is done, ahead of the
            next standard release.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <LatestReleaseLine />
          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-1">
              <Label htmlFor="from-last-release" className="flex items-center gap-1.5">
                <GitBranch className="size-3.5" /> Base on last release
              </Label>
              <p className="text-xs text-muted-foreground">
                Cuts a hotfix branch from the latest published tag so unreleased work on main does not ship
                with the fix. The fix is built on that branch and merged back into main after the release.
              </p>
              {hasPullRequest && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Unavailable: a pull request already targets main. The release will tag main instead.
                </p>
              )}
            </div>
            <Switch
              id="from-last-release"
              checked={fromLastRelease}
              disabled={hasPullRequest}
              onCheckedChange={setFromLastRelease}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={pending} onClick={() => onConfirm({ fromLastRelease })}>
            <Siren /> Create urgent release
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScheduleReleaseDialog({
  open,
  onOpenChange,
  issueIdentifier,
  scheduled,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueIdentifier?: string;
  /** Open scheduled releases a person can join instead of creating a new time. */
  scheduled: ReleaseView[];
  pending?: boolean;
  onConfirm: (choice: { releaseId: string } | { scheduledFor: string }) => void;
}) {
  const [when, setWhen] = useState(defaultScheduleLocal);
  useEffect(() => {
    if (open) setWhen(defaultScheduleLocal());
  }, [open]);
  const date = new Date(when);
  const valid = !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="size-4" /> Schedule a release
            {issueIdentifier ? ` for ${issueIdentifier}` : ""}
          </DialogTitle>
          <DialogDescription>
            The release ships at that time. Until then its pull requests stay out of main: the Merge agent
            holds them once they are approved and green.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {scheduled.length > 0 && (
            <div className="space-y-2">
              <Label>Join a scheduled release</Label>
              <ul className="space-y-1">
                {scheduled.map((release) => (
                  <li key={release.id}>
                    <Button
                      variant="outline"
                      className="w-full justify-between"
                      disabled={pending}
                      onClick={() => onConfirm({ releaseId: release.id })}
                    >
                      <span className="flex items-center gap-2">
                        <CalendarClock className="size-3.5" /> {formatSchedule(release.scheduledFor)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {release.issueCount} issue{release.issueCount === 1 ? "" : "s"}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="schedule-at">{scheduled.length ? "Or pick a new time" : "Ship at"}</Label>
            <Input
              id="schedule-at"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Your local time{valid ? ` · ${formatSchedule(date.toISOString())} in Sydney` : ""}. A release
              already scheduled for exactly this time is reused.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || pending}
            onClick={() => onConfirm({ scheduledFor: date.toISOString() })}
          >
            <CalendarClock /> Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ShipReleaseDialog({
  open,
  onOpenChange,
  release,
  suggestedTag,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  release: ReleaseView;
  suggestedTag: string | null;
  pending?: boolean;
  onConfirm: (tag: string | undefined) => void;
}) {
  const [tag, setTag] = useState("");
  useEffect(() => {
    if (open) setTag(release.tagLocked && release.tag ? release.tag : (suggestedTag ?? ""));
  }, [open, release.tagLocked, release.tag, suggestedTag]);
  const changed = tag.trim() !== (suggestedTag ?? "") && tag.trim() !== "";
  const notDone = release.issueCount === 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="size-4" /> Ship {release.name}
          </DialogTitle>
          <DialogDescription>
            Publishes the GitHub draft. GitHub creates the tag and the monorepo deploys from it. Held pull
            requests are merged first; the draft publishes once every issue in the release is done.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <LatestReleaseLine />
          <div className="space-y-2">
            <Label htmlFor="ship-tag">Tag</Label>
            <Input
              id="ship-tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder={suggestedTag ?? "v1.2.3"}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              {changed
                ? "A tag you set is kept as is."
                : "Suggested from the releases published in GitHub; recomputed at publish time so it never collides."}
            </p>
          </div>
          {notDone && (
            <p className="text-xs text-amber-600 dark:text-amber-400">This release has no issues yet.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={pending || notDone} onClick={() => onConfirm(changed ? tag.trim() : undefined)}>
            <Rocket /> Ship now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
